import { ErrorCode } from '@ps/web-core-enums';
import { select } from '@ps/web-core-store';
import { useTrackers } from '@ps/web-shared-analytics';
import { Label } from '@ps/web-shared-ui';
import classNames from 'clsx';
import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import React, { useEffect } from 'react';
import type { FieldInputProps, FieldMetaState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { CrossIcon, TickIcon } from '../../../assets/icons';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import HelperMessage from '../../HelperMessage/HelperMessage';
import InfoTooltip from '../../InfoTooltip/InfoTooltip';
import Translation from '../../Translation/Translation';
import WarningMessage from '../../WarningMessage/WarningMessage';
import { useTrackingLabel } from '../../hooks';
import useErrorLabels from '../../hooks/useErrorLabel/useErrorLabels';
import styles from '../../sharedStyles/RawInput.module.scss';
import { Validation } from '../Input/Input';
import type { Props as InputProps } from '../Input/Input';

interface Props extends InputProps {
  input?: FieldInputProps<Props['value'], HTMLElement>;
  meta?: FieldMetaState<string>;
}

const getClassNames = (
  {
    className,
    error,
    disabled,
    validationVariant
  }: Pick<Props, 'className' | 'error' | 'disabled' | 'validationVariant'>,
  meta?: FieldMetaState<Props['value']>
) => {
  if (!meta) {
    return className;
  }
  const showValidation = validationVariant !== Validation.Invisible;
  const listValidationHasError =
    (validationVariant === Validation.List || validationVariant === Validation.ImmediateList) &&
    (meta.touched || !!meta.initial);
  const inlineValidationHasError =
    validationVariant === Validation.Inline && (meta.touched || !!meta.initial);
  const immediateInlineValidationHasError =
    validationVariant === Validation.ImmediateInline &&
    (meta.visited || meta.initial) &&
    (!meta.pristine || meta.touched);
  const hasError =
    error ||
    (showValidation &&
      (inlineValidationHasError || immediateInlineValidationHasError || listValidationHasError) &&
      (!!meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)));
  const valid =
    showValidation && !meta.pristine && (meta.valid || (meta.dirtySinceLastSubmit && !meta.error));

  return (
    classNames(
      {
        [styles.disabled]: disabled,
        [styles.error]: hasError,
        [styles.valid]: valid
      },
      className
    ) || undefined
  );
};

const getIcon = (icon: React.FC<React.SVGAttributes<SVGElement>> | React.ReactElement) => {
  if (typeof icon === 'function') {
    return React.createElement(icon as React.FC<React.SVGAttributes<SVGElement>>, {
      className: styles.iconStart
    });
  } else {
    return icon;
  }
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const RawInput: React.FC<Props> & { styles: typeof styles } = ({
  autoComplete,
  autoFocus,
  children,
  className,
  disabled,
  error,
  helperMessage,
  hideLabel,
  icon,
  id,
  input,
  inputRef,
  label,
  labelClassName,
  labelContent,
  max,
  maxLength,
  meta,
  min,
  name,
  numericKeyboard,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  prefix,
  rawValue,
  readOnly,
  recordValidator,
  showValidationTick = true,
  startAdornment,
  step,
  tooltip,
  tooltipClassName,
  tooltipIconClassName,
  tooltipHover,
  type = 'text',
  validationErrors,
  validationVariant = Validation.Inline,
  values
}) => {
  const fieldName = useTrackingLabel(label, values);
  const { inputFilled, inputFocused, errorViewed } = useTrackers();
  const isMobile = useSelector(select.device.isMobile);
  const Element = type === 'textarea' ? 'textarea' : 'input';
  const metaErrors = meta?.error ? [meta.error] : undefined;
  const fieldErrors = useErrorLabels(validationErrors?.length ? validationErrors : metaErrors);

  useEffect(() => {
    if (meta?.submitError) errorViewed({ errorCode: ErrorCode.VALIDATION_ERROR as string });
  }, [meta?.submitError, errorViewed]);

  return (
    <div className={getClassNames({ className, error, disabled, validationVariant }, meta)}>
      <Label id={id} hidden={hideLabel} className={labelClassName}>
        {typeof label === 'object' ? <Translation {...label} values={values} /> : label}
        {tooltip && (
          <InfoTooltip
            iconClassName={tooltipIconClassName}
            content={tooltip}
            tooltipClassName={tooltipClassName}
            addHoverEffect={tooltipHover}
          />
        )}
        {labelContent}
      </Label>
      <div
        className={classNames(
          styles.inputWrapper,
          startAdornment && styles[`${startAdornment}Adornment`]
        )}
      >
        <Element
          {...input}
          {...(rawValue && { value: rawValue })}
          onChange={(event: ChangeEvent<any>) => {
            onChange?.(event);
            input?.onChange?.(event);
            if (event.target !== document.activeElement) {
              inputFilled({ fieldName, fieldType: type, isAutoFilled: true });
            }
          }}
          onFocus={(event: FocusEvent<any>) => {
            onFocus?.(event);
            input?.onFocus?.(event);
            inputFocused({ fieldName, fieldType: type });
          }}
          onBlur={(event: FocusEvent<any>) => {
            onBlur?.(event);
            input?.onBlur?.(event);
            input?.value && inputFilled({ fieldName, fieldType: type, fieldErrors }, input.value);
          }}
          onKeyDown={(event: KeyboardEvent<any>) => onKeyDown?.(event)}
          autoFocus={autoFocus && !isMobile}
          id={id}
          name={name ? name : id}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          className={styles.input}
          placeholder={placeholder}
          autoComplete={autoComplete}
          pattern={numericKeyboard ? '[0-9]*' : undefined}
          inputMode={numericKeyboard ? 'decimal' : undefined}
          max={max}
          min={min}
          step={step}
          maxLength={maxLength}
          ref={inputRef}
        />
        {showValidationTick &&
          meta &&
          meta.touched &&
          (meta.valid || (meta.dirtySinceLastSubmit && !meta.error) ? (
            <TickIcon className={styles.validationTick} />
          ) : (
            <CrossIcon className={styles.validationTick} />
          ))}
        {icon && getIcon(icon)}
        {prefix &&
          React.createElement(
            'div',
            {
              className: icon ? styles.iconPrefix : styles.iconStart
            },
            prefix
          )}
        {children}
        {!(error || (!recordValidator && meta?.touched && meta?.error)) && (
          <HelperMessage helperMessage={helperMessage} />
        )}
        <WarningMessage meta={meta} />
        <ErrorMessage
          meta={meta}
          input={input}
          validationVariant={validationVariant}
          validationErrors={validationErrors}
          hideError={!!recordValidator}
        />
      </div>
    </div>
  );
};

RawInput.styles = styles;

export default RawInput;
